import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import Info from '../../../../../../components/channel-configuration/info';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  OnDeviceErrorSubscription,
  OnDeviceErrorSubscriptionVariables,
  VersionedFeatureType,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_DEVICE_ERROR } from '../../../../../../data-access/subscriptions/lavva-devices';
import { toastError, toastInfo } from '../../../../../../utils/toast';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { useCheckDeviceSupport } from '../../../../hooks/version-map/use-check-device-support';
import { ChannelType } from '../../../../types';
import { useConfiguration } from '../../hooks/use-configuration';
import { InfoPageType } from '../../types';

type SwapInProps = {
  channel: ChannelType;
  setInfoPage: (value: React.SetStateAction<InfoPageType>) => void;
};

const SwapIn: React.FC<SwapInProps> = ({ channel, setInfoPage }) => {
  const { t } = useTranslation('configuration');
  const { t: tb } = useTranslation('backend-validation');
  const { t: td } = useTranslation('device-settings');
  const { checkSupport } = useCheckDeviceSupport();
  const { swapInDevice } = useConfiguration({ channel });
  const { skipLavvaFetch, selectedInstallationId } = useInstallationContext();
  const { deviceGenericInputs } = useDevicesAndChannelsContext();
  const { data: deviceErrorData } = useSubscription<OnDeviceErrorSubscription, OnDeviceErrorSubscriptionVariables>(
    ON_DEVICE_ERROR,
    {
      variables: { installationId: selectedInstallationId },
      skip: skipLavvaFetch,
    },
  );
  const inputs = deviceGenericInputs(channel.deviceId);

  useEffect(() => {
    if (deviceErrorData?.onDeviceError) {
      toastError({ content: tb(deviceErrorData.onDeviceError.code) });
    }
  }, [deviceErrorData]);

  const handleSwapIn = () => {
    const supported = checkSupport({
      feature: VersionedFeatureType.DeviceBindingsV2,
      supportKey: 'Core',
      currentVersion: channel.currentFirmwareVersion,
    });

    if (supported) swapInDevice();
    else toastInfo({ content: td('updateToUseFeature') });
  };

  if (!inputs.length) return null;

  return (
    <>
      <hr />
      <Info title={t('inService')} onClick={() => setInfoPage(InfoPageType.INSERVICE)} />
      <div className="row_container max-width-desktop">
        <button className="button button--secondary" onClick={handleSwapIn}>
          {t('reverseInputs')}
        </button>
      </div>
    </>
  );
};

export default SwapIn;
